// src/pages/ApplicationChampionshipMatches/components/MatchCreateModal/index.tsx
import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  Textarea,
  useToast,
  Box,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Control,
  Controller,
  FieldPath,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import { SelectField } from '@/components/SelectField';
import { ControlledSelect } from '@/components/ControlledSelect';
import ControlTeamSelectAutocomplete from '@/components/ControlTeamSelectAutocomplete';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { Club, HyperlinkTarget, MatchStatus, SeasonType } from '@/model';
import { GetParamsObject } from '@/model/query-params';
import { PostMatchV2Request } from '@/services/request/match_v2';
import { usePostMatchV2Mutation } from '@/services/queries/match_v2';
import { useChampionships } from '@/services/queries/championship';
import { useSeasons } from '@/services/queries/season';
import { useStages } from '@/services/queries/stage';
import { useSeasonTeams } from '@/services/queries/season-team';

const I18N_PREFIX = 'pages.application_championship_matches.components.match_create_modal';

export interface MatchCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDone: () => void;
}

type LastMatchField = {
  home_goals: string;
  away_goals: string;
  home_team_uuid: string;
  home_team_name: string;
  away_team_uuid: string;
  away_team_name: string;
};

type CreateFormData = {
  championship_uuid: string;
  season_uuid: string;
  stage_uuid: string;
  home_team_uuid: string;
  away_team_uuid: string;
  match_date: string;
  home_goals: string;
  away_goals: string;
  stadium: string;
  transmission: string;
  hyperlink: string;
  hyperlink_target: HyperlinkTarget;
  round: string;
  description: string;
  extra_time_home: string;
  extra_time_away: string;
  penalties_home: string;
  penalties_away: string;
  is_show_information: boolean;
  is_last_matches: boolean;
  last_matches: LastMatchField[];
  is_history_matches: boolean;
  history_matches: {
    wins: string;
    draw: string;
    lose: string;
  };
};

const DEFAULT_FORM: CreateFormData = {
  championship_uuid: '',
  season_uuid: '',
  stage_uuid: '',
  home_team_uuid: '',
  away_team_uuid: '',
  match_date: '',
  home_goals: '',
  away_goals: '',
  stadium: '',
  transmission: '',
  hyperlink: '',
  hyperlink_target: HyperlinkTarget.EXTERNAL,
  round: '',
  description: '',
  extra_time_home: '',
  extra_time_away: '',
  penalties_home: '',
  penalties_away: '',
  is_show_information: false,
  is_last_matches: false,
  last_matches: [],
  is_history_matches: false,
  history_matches: { wins: '0', draw: '0', lose: '0' },
};

const parseNumericOrNull = (value: string): number | null => {
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const NumericFormInput = ({
  control,
  name,
  width,
  variant = 'outline',
}: {
  control: Control<CreateFormData>;
  name: FieldPath<CreateFormData>;
  width?: string;
  variant?: 'outline' | 'flushed';
}) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => {
      const display = field.value == null ? '' : String(field.value);
      const visualProps =
        variant === 'flushed'
          ? ({ variant: 'flushed' as const } as const)
          : ({
              variant: 'outline' as const,
              bg: 'white',
              borderColor: 'gray.300',
              borderRadius: 8,
            } as const);
      return (
        <Input
          type='text'
          inputMode='numeric'
          textAlign='right'
          value={display}
          width={width}
          {...visualProps}
          onChange={event => {
            let digits = event.target.value.replace(/[^0-9]/g, '');
            if (digits.length > 1) digits = digits.replace(/^0+/, '') || '0';
            field.onChange(digits);
          }}
        />
      );
    }}
  />
);

export const MatchCreateModal = ({ isOpen, onClose, onDone }: MatchCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const postMatch = usePostMatchV2Mutation();

  const { control, register, handleSubmit, setValue, getValues, reset, watch } =
    useForm<CreateFormData>({ defaultValues: DEFAULT_FORM });

  const championshipUuid = watch('championship_uuid');
  const seasonUuid = watch('season_uuid');
  const stageUuid = watch('stage_uuid');
  const homeTeamUuid = watch('home_team_uuid');
  const awayTeamUuid = watch('away_team_uuid');
  const hasTicketLink = !!watch('hyperlink')?.trim();

  // Cascade lookups
  const championshipsParams = useMemo(
    () => GetParamsObject({} as Record<string, string>, 1, 200, 'asc', 'name'),
    [],
  );
  const { data: championshipsData } = useChampionships(championshipsParams);

  const seasonsParams = useMemo(
    () =>
      championshipUuid
        ? GetParamsObject(
            { championship_uuid: championshipUuid } as Record<string, string>,
            1,
            200,
            'desc',
            'year',
          )
        : undefined,
    [championshipUuid],
  );
  const { data: seasonsData } = useSeasons(seasonsParams);

  const stagesParams = useMemo(
    () =>
      seasonUuid
        ? GetParamsObject(
            { season_uuid: seasonUuid } as Record<string, string>,
            1,
            200,
            'asc',
            'stage_order',
          )
        : undefined,
    [seasonUuid],
  );
  const { data: stagesData } = useStages(stagesParams);

  // Times participantes (autocomplete) + cache pra last_matches
  const seasonTeamsParams = useMemo(
    () =>
      seasonUuid
        ? GetParamsObject(
            { season_uuid: seasonUuid } as Record<string, string>,
            1,
            200,
            'asc',
            'created_at',
          )
        : undefined,
    [seasonUuid],
  );
  const { data: seasonTeamsData } = useSeasonTeams(seasonTeamsParams, !!seasonUuid);

  const seasonClubsSeed: Club[] = useMemo(() => {
    const map = new Map<string, Club>();
    (seasonTeamsData?.data ?? []).forEach(st => {
      const team = st.team;
      const uuid = st.team_uuid ?? team?.uuid;
      if (!team || !uuid || map.has(uuid)) return;
      map.set(uuid, {
        uuid,
        name: team.name,
        acronym: team.acronym,
        logo_path: team.logo_path ?? '',
        created_at: '',
        updated_at: '',
      });
    });
    return Array.from(map.values());
  }, [seasonTeamsData]);

  // Para last_matches: SOMENTE os 2 times do confronto atual (escolhidos no topo).
  const matchTeamsByUuid = useMemo(() => {
    const map = new Map<string, { uuid: string; name: string; logo?: string }>();
    seasonClubsSeed.forEach(club => {
      if (club.uuid === homeTeamUuid || club.uuid === awayTeamUuid) {
        map.set(club.uuid, { uuid: club.uuid, name: club.name, logo: club.logo_path });
      }
    });
    return map;
  }, [seasonClubsSeed, homeTeamUuid, awayTeamUuid]);

  const matchTeamOptions = useMemo(
    () =>
      Array.from(matchTeamsByUuid.values()).map(team => ({
        value: team.uuid,
        label: team.name,
        imageUrl: team.logo ?? '',
      })),
    [matchTeamsByUuid],
  );

  const oppositeUuid = (uuid: string): string => {
    for (const teamUuid of matchTeamsByUuid.keys()) {
      if (teamUuid !== uuid) return teamUuid;
    }
    return '';
  };

  const { fields: lastMatchesFields, append: appendLastMatch, remove: removeLastMatch } =
    useFieldArray({ control, name: 'last_matches' });

  const isLastMatches = watch('is_last_matches');
  const isHistoryMatches = watch('is_history_matches');

  /** Trocar Championship/Season limpa selects encadeados. */
  const handleChampionshipChange = (value: string) => {
    setValue('championship_uuid', value);
    setValue('season_uuid', '');
    setValue('stage_uuid', '');
    setValue('home_team_uuid', '');
    setValue('away_team_uuid', '');
  };
  const handleSeasonChange = (value: string) => {
    setValue('season_uuid', value);
    setValue('stage_uuid', '');
    setValue('home_team_uuid', '');
    setValue('away_team_uuid', '');
  };

  const handleClose = () => {
    reset(DEFAULT_FORM);
    onClose();
  };

  const submitWithStatus = (status: MatchStatus) => {
    return handleSubmit(data => {
      if (!data.stage_uuid) {
        toast({ status: 'error', title: t(`${I18N_PREFIX}.errors.missing_stage`) });
        return;
      }
      if (!data.home_team_uuid || !data.away_team_uuid) {
        toast({ status: 'error', title: t(`${I18N_PREFIX}.errors.missing_teams`) });
        return;
      }
      if (data.home_team_uuid === data.away_team_uuid) {
        toast({ status: 'error', title: t(`${I18N_PREFIX}.errors.same_team`) });
        return;
      }

      const normalizedLastMatches = (data.last_matches ?? [])
        .map(lm => ({
          home_goals: lm.home_goals === '' ? 0 : Number(lm.home_goals) || 0,
          away_goals: lm.away_goals === '' ? 0 : Number(lm.away_goals) || 0,
          home_team_uuid: lm.home_team_uuid,
          home_team_name:
            matchTeamsByUuid.get(lm.home_team_uuid)?.name ?? lm.home_team_name ?? '',
          away_team_uuid: lm.away_team_uuid,
          away_team_name:
            matchTeamsByUuid.get(lm.away_team_uuid)?.name ?? lm.away_team_name ?? '',
        }))
        .filter(lm => lm.home_team_uuid && lm.away_team_uuid);

      const payload: PostMatchV2Request = {
        stage_uuid: data.stage_uuid,
        home_team_uuid: data.home_team_uuid,
        away_team_uuid: data.away_team_uuid,
        match_date: data.match_date ? new Date(data.match_date).toISOString() : null,
        stadium: data.stadium.trim() || null,
        transmission: data.transmission.trim() || null,
        hyperlink: data.hyperlink.trim() || null,
        hyperlink_target: data.hyperlink_target,
        description: data.description.trim() || null,
        round: parseNumericOrNull(data.round),
        home_goals: parseNumericOrNull(data.home_goals),
        away_goals: parseNumericOrNull(data.away_goals),
        extra_time_home: parseNumericOrNull(data.extra_time_home),
        extra_time_away: parseNumericOrNull(data.extra_time_away),
        penalties_home: parseNumericOrNull(data.penalties_home),
        penalties_away: parseNumericOrNull(data.penalties_away),
        status,
        is_show_information: data.is_show_information,
        is_last_matches: data.is_last_matches,
        last_matches: data.is_last_matches ? normalizedLastMatches : null,
        is_history_matches: data.is_history_matches,
        history_matches: data.is_history_matches
          ? {
              wins:
                data.history_matches.wins === '' ? 0 : Number(data.history_matches.wins) || 0,
              draw:
                data.history_matches.draw === '' ? 0 : Number(data.history_matches.draw) || 0,
              lose:
                data.history_matches.lose === '' ? 0 : Number(data.history_matches.lose) || 0,
            }
          : null,
      };

      postMatch.mutate(payload, {
        onSuccess: () => {
          toast({ status: 'success', title: t(`${I18N_PREFIX}.toasts.create_success`) });
          reset(DEFAULT_FORM);
          onDone();
        },
        onError: (error: unknown) => {
          toast({
            status: 'error',
            title:
              (error as Error)?.message || t(`${I18N_PREFIX}.toasts.create_error`),
          });
        },
      });
    })();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='3xl' isCentered scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize='medium' fontWeight='bold'>
            {t(`${I18N_PREFIX}.title`)}
          </Text>
        </ModalHeader>
        <ModalBody>
          <Flex direction='column' gap={4}>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_championship`)}</Text>
                <SelectField
                  value={championshipUuid}
                  onChange={event => handleChampionshipChange(event.target.value)}
                >
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(championshipsData?.data ?? []).map(champ => (
                    <option key={champ.uuid} value={champ.uuid}>
                      {champ.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_season`)}</Text>
                <SelectField
                  value={seasonUuid}
                  isDisabled={!championshipUuid}
                  onChange={event => handleSeasonChange(event.target.value)}
                >
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(seasonsData?.data ?? []).map(season => (
                    <option key={season.uuid} value={season.uuid}>
                      {season.year}
                      {season.type === SeasonType.HYBRID ? ` · ${t(`${I18N_PREFIX}.season_type_hybrid`)}` : ''}
                      {season.type === SeasonType.KNOCKOUT ? ` · ${t(`${I18N_PREFIX}.season_type_knockout`)}` : ''}
                      {season.type === SeasonType.LEAGUE ? ` · ${t(`${I18N_PREFIX}.season_type_league`)}` : ''}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_stage`)}</Text>
                <SelectField
                  value={stageUuid}
                  isDisabled={!seasonUuid}
                  onChange={event => {
                    setValue('stage_uuid', event.target.value);
                  }}
                >
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(stagesData?.data ?? []).map(stage => (
                    <option key={stage.uuid} value={stage.uuid}>
                      {stage.group_name ? `${stage.name} · ${stage.group_name}` : stage.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <ControlTeamSelectAutocomplete<CreateFormData, 'home_team_uuid'>
                control={control}
                name='home_team_uuid'
                clubs={seasonClubsSeed}
                width='100%'
                label={t(`${I18N_PREFIX}.field_home_team`)}
                placeholder={t(`${I18N_PREFIX}.placeholder_team`)}
                disabled={!seasonUuid}
                showFlag
                usePortal={false}
                prefetchOnOpen={false}
                minChars={9999}
              />
              <ControlTeamSelectAutocomplete<CreateFormData, 'away_team_uuid'>
                control={control}
                name='away_team_uuid'
                clubs={seasonClubsSeed}
                width='100%'
                label={t(`${I18N_PREFIX}.field_away_team`)}
                placeholder={t(`${I18N_PREFIX}.placeholder_team`)}
                disabled={!seasonUuid}
                showFlag
                usePortal={false}
                prefetchOnOpen={false}
                minChars={9999}
              />

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_date`)}</Text>
                <Controller
                  control={control}
                  name='match_date'
                  render={({ field }) => (
                    <Input type='datetime-local' value={field.value} onChange={field.onChange} />
                  )}
                />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_round`)}</Text>
                <NumericFormInput control={control} name='round' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_home_goals`)}</Text>
                <NumericFormInput control={control} name='home_goals' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_away_goals`)}</Text>
                <NumericFormInput control={control} name='away_goals' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_stadium`)}</Text>
                <Input {...register('stadium')} />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_transmission`)}</Text>
                <Input {...register('transmission')} />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_extra_time_home`)}</Text>
                <NumericFormInput control={control} name='extra_time_home' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_extra_time_away`)}</Text>
                <NumericFormInput control={control} name='extra_time_away' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_penalties_home`)}</Text>
                <NumericFormInput control={control} name='penalties_home' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_penalties_away`)}</Text>
                <NumericFormInput control={control} name='penalties_away' variant='flushed' />
              </InputGroup>
            </Grid>

            <Box borderWidth='1px' borderColor='gray.200' borderRadius={8} p={4}>
              <Flex direction='column' gap={4}>
                <Text fontWeight='bold'>{t(`${I18N_PREFIX}.tickets_section_title`)}</Text>

                <InputGroup display='flex' flexDirection='column'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_hyperlink`)}</Text>
                  <Input {...register('hyperlink')} placeholder='https://' />
                </InputGroup>

                {/* Config dependente do link: sem link não há o que abrir. */}
                <InputGroup
                  display='flex'
                  flexDirection='column'
                  gap={2}
                  pl={4}
                  borderLeft='2px solid'
                  borderColor='gray.200'
                >
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_hyperlink_target`)}</Text>
                  <SelectField {...register('hyperlink_target')} isDisabled={!hasTicketLink}>
                    <option value={HyperlinkTarget.EXTERNAL}>
                      {t(`${I18N_PREFIX}.hyperlink_target_external`)}
                    </option>
                    <option value={HyperlinkTarget.INTERNAL}>
                      {t(`${I18N_PREFIX}.hyperlink_target_internal`)}
                    </option>
                    <option value={HyperlinkTarget.PARTNER}>
                      {t(`${I18N_PREFIX}.hyperlink_target_partner`)}
                    </option>
                  </SelectField>
                  <Text fontSize='sm' color='gray.500'>
                    {hasTicketLink
                      ? t(`${I18N_PREFIX}.hyperlink_target_helper`)
                      : t(`${I18N_PREFIX}.hyperlink_target_helper_empty`)}
                  </Text>
                </InputGroup>
              </Flex>
            </Box>

            <InputGroup display='flex' flexDirection='column'>
              <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_description`)}</Text>
              <Textarea rows={3} {...register('description')} />
            </InputGroup>

            <Controller
              name='is_show_information'
              control={control}
              render={({ field }) => (
                <Checkbox isChecked={field.value} onChange={field.onChange}>
                  {t(`${I18N_PREFIX}.field_is_show_information`)}
                </Checkbox>
              )}
            />

            <Controller
              name='is_last_matches'
              control={control}
              render={({ field }) => (
                <FormControl display='flex' gap={2} alignItems='center'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.last_matches_label`)}</Text>
                  <Switch isChecked={field.value} onChange={field.onChange} />
                </FormControl>
              )}
            />

            {isLastMatches ? (
              <Flex direction='column' gap={2}>
                {lastMatchesFields.map((field, index) => (
                  <Flex
                    key={field.id}
                    direction={{ base: 'column', sm: 'row' }}
                    gap={4}
                    alignItems={{ sm: 'flex-start' }}
                    position='relative'
                  >
                    <ControlledSelect
                      variant='outline'
                      control={control}
                      width='180px'
                      name={`last_matches.${index}.home_team_uuid`}
                      options={matchTeamOptions}
                      onSelect={value => {
                        const opposite = oppositeUuid(value);
                        if (opposite) {
                          setValue(`last_matches.${index}.away_team_uuid`, opposite, {
                            shouldDirty: true,
                          });
                        }
                      }}
                    />
                    <Flex alignItems='center' gap={1}>
                      <NumericFormInput
                        control={control}
                        name={`last_matches.${index}.home_goals`}
                        width='50px'
                      />
                      <ChakraFeatherIcon name='x' width='18px' />
                      <NumericFormInput
                        control={control}
                        name={`last_matches.${index}.away_goals`}
                        width='50px'
                      />
                    </Flex>
                    <ControlledSelect
                      variant='outline'
                      control={control}
                      width='180px'
                      name={`last_matches.${index}.away_team_uuid`}
                      options={matchTeamOptions}
                      onSelect={value => {
                        const opposite = oppositeUuid(value);
                        if (opposite) {
                          setValue(`last_matches.${index}.home_team_uuid`, opposite, {
                            shouldDirty: true,
                          });
                        }
                      }}
                    />
                    <IconButton
                      aria-label={t(`${I18N_PREFIX}.remove_last_match_aria`)}
                      icon={<ChakraFeatherIcon name='trash-2' boxSize='14px' />}
                      variant='ghost'
                      color='red.500'
                      size='xs'
                      alignSelf='center'
                      onClick={() => removeLastMatch(index)}
                    />
                  </Flex>
                ))}
                <Button
                  variant='link'
                  color='brand.500'
                  textDecor='underline'
                  alignSelf='flex-start'
                  leftIcon={<ChakraFeatherIcon name='plus' />}
                  onClick={() => {
                    const home = getValues('home_team_uuid');
                    const away = getValues('away_team_uuid');
                    appendLastMatch({
                      home_team_uuid: home,
                      home_team_name: matchTeamsByUuid.get(home)?.name ?? '',
                      home_goals: '0',
                      away_team_uuid: away,
                      away_team_name: matchTeamsByUuid.get(away)?.name ?? '',
                      away_goals: '0',
                    });
                  }}
                >
                  {t(`${I18N_PREFIX}.add_last_match_button`)}
                </Button>
              </Flex>
            ) : null}

            <Controller
              name='is_history_matches'
              control={control}
              render={({ field }) => (
                <FormControl display='flex' gap={2} alignItems='center'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.history_matches_label`)}</Text>
                  <Switch isChecked={field.value} onChange={field.onChange} />
                </FormControl>
              )}
            />

            {isHistoryMatches ? (
              <Flex gap={6}>
                <FormControl display='flex' gap={2} alignItems='center' width='fit-content'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.wins_label`)}</Text>
                  <NumericFormInput control={control} name='history_matches.wins' width='60px' />
                </FormControl>
                <FormControl display='flex' gap={2} alignItems='center' width='fit-content'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.draw_label`)}</Text>
                  <NumericFormInput control={control} name='history_matches.draw' width='60px' />
                </FormControl>
                <FormControl display='flex' gap={2} alignItems='center' width='fit-content'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.lose_label`)}</Text>
                  <NumericFormInput control={control} name='history_matches.lose' width='60px' />
                </FormControl>
              </Flex>
            ) : null}
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Flex w='100%' justifyContent='space-between' alignItems='center' gap={2}>
            <Button
              variant='link'
              color='brand.500'
              textDecoration='underline'
              leftIcon={<ChakraFeatherIcon name='save' />}
              onClick={() => submitWithStatus(MatchStatus.DRAFT)}
              isDisabled={postMatch.isPending}
            >
              {t(`${I18N_PREFIX}.save_draft_button`)}
            </Button>

            <Flex gap={3}>
              <Button variant='outline' color='gray.600' onClick={handleClose} isDisabled={postMatch.isPending}>
                {t(`${I18N_PREFIX}.cancel_button`)}
              </Button>
              <Button
                bg='green.500'
                color='white'
                _hover={{ bg: 'green.600' }}
                px={10}
                onClick={() => submitWithStatus(MatchStatus.SCHEDULED)}
                isLoading={postMatch.isPending}
              >
                {t(`${I18N_PREFIX}.create_button`)}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
